import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  Download,
  Loader2,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { useOrg } from "@/lib/org/OrgContext";
import { useToastMutation } from "@/lib/mutations";
import { ApiError } from "@/api/fetch";
import {
  contractsApi,
  contractsQueries,
  ContractStatus,
  statusLabel,
  type ContractPreviewDto,
} from "@/lib/contracts/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageContainer } from "@/components/layout/WorkspaceShell";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/o/$slug/contracts/$contractId")({
  component: ContractDetailPage,
});

function ContractDetailPage() {
  const org = useOrg();
  const { contractId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const contract = useQuery(contractsQueries.detail(contractId));

  const setData = (next: ContractPreviewDto) =>
    queryClient.setQueryData(contractsQueries.detail(contractId).queryKey, next);

  const confirm = useToastMutation({
    mutationFn: () => contractsApi.confirm(contractId),
    toasts: { success: "Vertrag bestätigt." },
    onSuccess: setData,
  });
  const retryWage = useToastMutation({
    mutationFn: () => contractsApi.retryWage(contractId),
    toasts: { success: "Lohnregel-Anlage erneut versucht." },
    onSuccess: setData,
  });
  const remove = useToastMutation({
    mutationFn: () => contractsApi.remove(contractId),
    toasts: { success: "Entwurf gelöscht." },
    onSuccess: () =>
      void navigate({ to: "/o/$slug/contracts", params: { slug: org.slug } }),
  });

  return (
    <PageContainer className="max-w-5xl">
      <div className="mb-4">
        <Link
          to="/o/$slug/contracts"
          params={{ slug: org.slug }}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Verträge
        </Link>
      </div>

      {contract.isLoading && <DetailSkeleton />}
      {contract.error && <DetailError error={contract.error as Error} />}

      {contract.data && (
        <ContractView
          contract={contract.data}
          onConfirm={() => confirm.mutate(undefined)}
          onRetryWage={() => retryWage.mutate(undefined)}
          onDelete={() => {
            if (window.confirm("Entwurf wirklich löschen?")) remove.mutate(undefined);
          }}
          confirming={confirm.isPending}
          retrying={retryWage.isPending}
          deleting={remove.isPending}
        />
      )}
    </PageContainer>
  );
}

function ContractView({
  contract,
  onConfirm,
  onRetryWage,
  onDelete,
  confirming,
  retrying,
  deleting,
}: {
  contract: ContractPreviewDto;
  onConfirm: () => void;
  onRetryWage: () => void;
  onDelete: () => void;
  confirming: boolean;
  retrying: boolean;
  deleting: boolean;
}) {
  const isDraft = contract.status === ContractStatus.Draft;
  const isConfirmed = contract.status >= ContractStatus.Confirmed;
  const wageFailed = contract.ordioWageFailedAt !== null;

  return (
    <>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              {contract.employeeDisplayName}
            </h1>
            <StatusBadge status={contract.status} />
            <KindBadge kind={contract.kind} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {contract.contractType} · {contract.companyName} · erstellt{" "}
            {formatRelative(contract.createdAt) ?? "—"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline">
            <a
              href={contractsApi.pdfUrl(contract.id, isConfirmed ? "confirmed" : "draft")}
              target="_blank"
              rel="noreferrer"
            >
              <Download className="h-4 w-4" />
              PDF
            </a>
          </Button>
          {isDraft && (
            <Button onClick={onConfirm} disabled={confirming}>
              {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Bestätigen
            </Button>
          )}
          {wageFailed && (
            <Button variant="outline" onClick={onRetryWage} disabled={retrying}>
              {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              Lohnregel erneut versuchen
            </Button>
          )}
          {isDraft && (
            <Button
              variant="outline"
              onClick={onDelete}
              disabled={deleting}
              className="text-destructive"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Löschen
            </Button>
          )}
        </div>
      </header>

      {wageFailed && (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span className="font-medium">Lohnregel-Anlage fehlgeschlagen</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Letzter Versuch {formatRelative(contract.ordioWageFailedAt) ?? "—"}. „Erneut versuchen"
            sendet die Wage-Anfrage erneut an Ordio.
          </p>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
        <div className="space-y-4">
          <FieldsCard fieldValues={contract.fieldValues} />
          <PartiesCard contract={contract} />
        </div>
        <SidePanel contract={contract} />
      </div>
    </>
  );
}

// ── Cards ───────────────────────────────────────────────────────────────────

function FieldsCard({ fieldValues }: { fieldValues: Record<string, string> }) {
  const entries = Object.entries(fieldValues);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Vertragsfelder</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {entries.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">Keine Felder.</p>
        ) : (
          <dl className="divide-y">
            {entries.map(([k, v]) => (
              <div key={k} className="flex items-start justify-between gap-4 px-4 py-2 text-sm">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="text-right">{v || "—"}</dd>
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function PartiesCard({ contract }: { contract: ContractPreviewDto }) {
  const e = contract.employee;
  const er = contract.employer;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Parteien</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <div>
          <h3 className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
            Arbeitnehmer:in
          </h3>
          <div className="text-sm">
            <div className="font-medium">
              {[e.salutation, e.firstName, e.lastName].filter(Boolean).join(" ")}
            </div>
            <div className="text-xs text-muted-foreground">{e.email ?? "—"}</div>
            <div className="text-xs text-muted-foreground">{e.phone ?? "—"}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              {[e.addressStreet, e.addressZip, e.addressCity].filter(Boolean).join(", ") || "—"}
            </div>
          </div>
        </div>
        <div>
          <h3 className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
            Arbeitgeber
          </h3>
          <div className="text-sm">
            <div className="font-medium">{er.name}</div>
            <div className="text-xs text-muted-foreground">
              {[er.address, er.postcode, er.city].filter(Boolean).join(", ") || "—"}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {er.legalRepresentativeName
                ? `Vertreten durch ${er.legalRepresentativeName}`
                : "Ohne Unterzeichner"}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SidePanel({ contract }: { contract: ContractPreviewDto }) {
  return (
    <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
      <div className="rounded-lg border bg-card p-4 text-sm">
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground">Status</h3>
        <div className="mt-1 text-xs">
          <Row label="Status" value={statusLabel(contract.status)} />
          <Row label="Erstellt" value={formatRelative(contract.createdAt) ?? "—"} />
          <Row label="Bestätigt" value={formatRelative(contract.confirmedAt) ?? "—"} />
          <Row
            label="Unterschrieben"
            value={formatRelative(contract.employeeSignedAt) ?? "—"}
          />
        </div>
        <Separator className="my-3" />
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground">Ordio-Wage</h3>
        <div className="mt-1 text-xs">
          <Row
            label="Status"
            value={
              contract.wageCreated
                ? "Erstellt"
                : contract.ordioWageFailedAt
                  ? "Fehlgeschlagen"
                  : "Noch nicht versucht"
            }
          />
          {contract.ordioWageId && <Row label="Wage-ID" value={contract.ordioWageId} />}
          {contract.employmentId && (
            <Row label="Arbeitsverhältnis" value={contract.employmentId.slice(0, 8) + "…"} />
          )}
        </div>
      </div>
    </aside>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="mt-1 flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="truncate text-right">{value}</span>
    </div>
  );
}

// ── Bits ────────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ContractStatus }) {
  const variants: Record<ContractStatus, string> = {
    [ContractStatus.Draft]: "bg-muted text-muted-foreground",
    [ContractStatus.Confirmed]: "bg-emerald-100 text-emerald-700",
    [ContractStatus.Sent]: "bg-blue-100 text-blue-700",
    [ContractStatus.SendFailed]: "bg-amber-100 text-amber-800",
    [ContractStatus.Signed]: "bg-emerald-100 text-emerald-700",
    [ContractStatus.Archived]: "bg-muted text-muted-foreground",
  };
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-xs", variants[status])}>
      {statusLabel(status)}
    </span>
  );
}

function KindBadge({ kind }: { kind: ContractPreviewDto["kind"] }) {
  if (kind === "NewContract") return null;
  return (
    <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-700">
      {kind === "Amendment" ? "Vertragsergänzung" : "Adoption"}
    </span>
  );
}

function DetailSkeleton() {
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
      <div className="space-y-3">
        <Skeleton className="h-12" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
      <Skeleton className="h-48" />
    </div>
  );
}

function DetailError({ error }: { error: Error }) {
  const status = error instanceof ApiError ? error.status : null;
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
      {status === 404
        ? "Vertrag nicht gefunden."
        : status === 403
          ? "Keine Berechtigung."
          : `Konnte Vertrag nicht laden: ${error.message}`}
    </div>
  );
}
